import { useCallback, useEffect, useState } from 'react';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { Button } from 'common/ui/Button';
import { Stack } from 'common/ui/Layout';
import { useWorkstationID } from 'hooks/useWorkstationID';
import { Colors } from 'styles';

export const ReporterWorkstationIDSettings = (): React.ReactElement => {
  const { workstationID, saveWorkstationID } = useWorkstationID();
  const [workstationIDDraft, setWorkstationIDDraft] = useState(workstationID ?? '');
  const [workstationIDError, setWorkstationIDError] = useState<string | null>(null);

  const savedWorkstationID = workstationID ?? '';
  const hasWorkstationIDDraftChanges = workstationIDDraft !== savedWorkstationID;

  useEffect(() => {
    setWorkstationIDDraft(workstationID ?? '');
    setWorkstationIDError(null);
  }, [workstationID]);

  const handleSaveWorkstationID = useCallback(() => {
    const result = saveWorkstationID(workstationIDDraft);

    if (result.ok === false) {
      setWorkstationIDError(result.error);
      return;
    }

    setWorkstationIDDraft(result.workstationID);
    setWorkstationIDError(null);
  }, [saveWorkstationID, workstationIDDraft]);

  const handleCancelWorkstationID = useCallback(() => {
    setWorkstationIDDraft(savedWorkstationID);
    setWorkstationIDError(null);
  }, [savedWorkstationID]);

  return (
    <Stack vertical space="xsmall" data-testid="reporter-workstation-id-section">
      <Text variant="display1" display="block">
        Workstation ID
      </Text>
      <Stack vertical space="xxsmall">
        <Text variant="caption" color="secondary-alt">
          Set the workstation ID stored in this browser for reporter workflows.
        </Text>
        <Text variant="caption" color="secondary-alt">
          Leading and trailing spaces are trimmed when saved.
        </Text>
      </Stack>
      <Stack alignY="center" space="small">
        <Input
          fullWidth
          aria-label="Workstation ID"
          value={workstationIDDraft}
          placeholder="Enter workstation ID"
          onChange={(event) => {
            setWorkstationIDDraft(event.currentTarget.value);
            if (workstationIDError != null) {
              setWorkstationIDError(null);
            }
          }}
          borderColor={workstationIDError != null ? Colors.red5 : undefined}
          data-testid="reporter-workstation-id-input"
        />
        {hasWorkstationIDDraftChanges && (
          <Stack space="small">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancelWorkstationID}
              data-testid="cancel-reporter-workstation-id"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveWorkstationID}
              data-testid="save-reporter-workstation-id"
            >
              Save
            </Button>
          </Stack>
        )}
      </Stack>
      <div
        css={`
          min-height: 2rem;
          display: flex;
          align-items: flex-start;
        `}
      >
        {workstationIDError != null ? (
          <Text color="error" variant="caption" data-testid="reporter-workstation-id-error">
            {workstationIDError}
          </Text>
        ) : hasWorkstationIDDraftChanges ? (
          <Text variant="caption" color="secondary-alt" data-testid="workstation-id-dirty">
            Unsaved changes.
          </Text>
        ) : null}
      </div>
    </Stack>
  );
};
